import { useEffect, useRef } from 'react';

let burstMinCount = 1;
let burstMaxCount = 4;
let burstGapMs = 150;
let burstPauseMinMs = 700;
let burstPauseMaxMs = 2600;
let rocketMinTargetY = 0.1;
let rocketMaxTargetY = 0.4;
let targetYOffsetPx = 60;
let rocketGravity = 0.06;
let rocketMinExplodeVy = -0.5;
let rocketMaxExplodeVy = 0.5;
let rocketHangFrames = 30;
let rocketHangDamping = 0.9;
let rocketLaunchMinX = 0.1;
let rocketLaunchMaxX = 0.9;
let rocketAngleJitterRad = 0.3;
let rocketMinSpeed = 5;
let rocketMaxSpeed = 9;
let rocketMinSize = 2;
let rocketMaxSize = 4;
let rocketTrailLength = 15;
let explosionMinParticles = 50;
let explosionMaxParticles = 120;
let particleGravity = 0.05;
let particleFriction = 0.98;
let particleMinDecay = 0.008;
let particleMaxDecay = 0.02;
let particleMinSpeed = 2;
let particleMaxSpeed = 6;
let particleMinSize = 1;
let particleMaxSize = 4;

let rand = (min: number, max: number) => Math.random() * (max - min) + min;
let randInt = (min: number, max: number) => Math.floor(rand(min, max));

type Particle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  decay: number;
  size: number;
  color: string;
};

type Rocket = {
  x: number;
  y: number;
  targetY: number;
  vx: number;
  vy: number;
  size: number;
  color: string;
  trail: { x: number; y: number }[];
  explodeVy: number;
  hanging: boolean;
  hangTimer: number;
};

let createParticle = (x: number, y: number, color: string): Particle => {
  let angle = rand(0, Math.PI * 2);
  let speed = rand(particleMinSpeed, particleMaxSpeed);
  return {
    x,
    y,
    vx: Math.cos(angle) * speed,
    vy: Math.sin(angle) * speed,
    life: 1,
    decay: rand(particleMinDecay, particleMaxDecay),
    size: rand(particleMinSize, particleMaxSize),
    color,
  };
};

let createRocket = (
  w: number,
  h: number,
  color: string,
  bounds?: { top: number; bottom: number },
): Rocket => {
  let angle = -Math.PI / 2 + rand(-rocketAngleJitterRad, rocketAngleJitterRad);
  let speed = rand(rocketMinSpeed, rocketMaxSpeed);
  let offset = bounds ? bounds.top : targetYOffsetPx;
  return {
    x: rand(w * rocketLaunchMinX, w * rocketLaunchMaxX),
    y: bounds?.bottom ?? h,
    targetY: rand(h * rocketMinTargetY, h * rocketMaxTargetY) + offset,
    vx: Math.cos(angle) * speed,
    vy: Math.sin(angle) * speed,
    size: rand(rocketMinSize, rocketMaxSize),
    color,
    trail: [],
    explodeVy: rand(rocketMinExplodeVy, rocketMaxExplodeVy),
    hanging: false,
    hangTimer: 0,
  };
};

let updateRocket = (r: Rocket): 'alive' | 'hang' | 'explode' => {
  r.trail.push({ x: r.x, y: r.y });
  if (r.trail.length > rocketTrailLength) r.trail.shift();
  r.x += r.vx;
  r.y += r.vy;

  if (r.hanging) {
    r.vx *= rocketHangDamping;
    r.vy *= rocketHangDamping;
    r.hangTimer++;
    if (r.hangTimer >= rocketHangFrames) return 'explode';
    return 'hang';
  }

  r.vy += rocketGravity;

  if (r.vy >= r.explodeVy || r.y <= r.targetY) {
    r.hanging = true;
    return 'hang';
  }

  return 'alive';
};

let drawRocket = (ctx: CanvasRenderingContext2D, r: Rocket) => {
  ctx.save();
  if (r.hanging) {
    let t = r.hangTimer / rocketHangFrames;
    ctx.globalAlpha = Math.pow(1 - t, 2);
  }
  ctx.beginPath();
  if (r.trail.length > 1) {
    ctx.moveTo(r.trail[0]!.x, r.trail[0]!.y);
    for (let point of r.trail) ctx.lineTo(point.x, point.y);
  } else {
    ctx.moveTo(r.x, r.y);
    ctx.lineTo(r.x, r.y);
  }
  ctx.strokeStyle = r.color;
  ctx.lineWidth = r.size;
  ctx.lineCap = 'round';
  ctx.stroke();
  ctx.restore();
};

let explodeRocket = (r: Rocket): Particle[] => {
  let count = randInt(explosionMinParticles, explosionMaxParticles);
  let result: Particle[] = [];
  for (let i = 0; i < count; i++) {
    result.push(createParticle(r.x, r.y, r.color));
  }
  return result;
};

let updateParticle = (p: Particle) => {
  p.vx *= particleFriction;
  p.vy *= particleFriction;
  p.vy += particleGravity;
  p.x += p.vx;
  p.y += p.vy;
  p.life -= p.decay;
};

let drawParticle = (ctx: CanvasRenderingContext2D, p: Particle) => {
  ctx.save();
  ctx.globalAlpha = Math.pow(p.life, 0.7);
  ctx.beginPath();
  ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
  ctx.fillStyle = p.color;
  ctx.fill();
  ctx.restore();
};

export type FireworkTargetBounds = (canvas: { width: number; height: number }) => {
  top: number;
  bottom: number;
};

let Fireworks = ({
  colors,
  active,
  className,
  targetBounds,
}: {
  colors: string[];
  active: boolean;
  className?: string;
  targetBounds?: FireworkTargetBounds;
}) => {
  let canvasRef = useRef<HTMLCanvasElement>(null);
  let activeRef = useRef(active);
  activeRef.current = active;
  let colorsRef = useRef(colors);
  colorsRef.current = colors;

  useEffect(() => {
    let canvas = canvasRef.current;
    if (!canvas) return;
    let ctx = canvas.getContext('2d');
    if (!ctx) return;

    let rockets: Rocket[] = [];
    let particles: Particle[] = [];
    let launchTimeout: ReturnType<typeof setTimeout>;
    let frameId: number;

    let setCanvasSize = () => {
      canvas!.width = window.innerWidth;
      canvas!.height = window.innerHeight;
    };
    setCanvasSize();
    window.addEventListener('resize', setCanvasSize);

    let pickColor = () =>
      colorsRef.current[randInt(0, colorsRef.current.length)] || '#f19939';

    let lastTime = performance.now();
    let tabVisible = true;
    let onVisibilityChange = () => {
      tabVisible = document.visibilityState === 'visible';
      if (tabVisible) lastTime = performance.now();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    let launchBurst = () => {
      if (!activeRef.current || !tabVisible) {
        launchTimeout = setTimeout(launchBurst, burstPauseMinMs);
        return;
      }
      let count = randInt(burstMinCount, burstMaxCount + 1);
      for (let i = 0; i < count; i++) {
        setTimeout(() => {
          rockets.push(
            createRocket(
              canvas!.width,
              canvas!.height,
              pickColor(),
              targetBounds?.({ width: canvas!.width, height: canvas!.height }),
            ),
          );
        }, i * burstGapMs);
      }
      let pause = rand(burstPauseMinMs, burstPauseMaxMs);
      launchTimeout = setTimeout(launchBurst, count * burstGapMs + pause);
    };
    launchBurst();

    let animate = () => {
      let now = performance.now();
      let elapsed = now - lastTime;
      lastTime = now;
      let stepsNeeded = Math.min(Math.round(elapsed / (1000 / 120)), 10);

      for (let s = 0; s < stepsNeeded; s++) {
        for (let i = rockets.length - 1; i >= 0; i--) {
          let r = rockets[i]!;
          let state = updateRocket(r);
          if (state === 'explode') {
            particles.push(...explodeRocket(r));
            rockets.splice(i, 1);
          }
        }
        for (let i = particles.length - 1; i >= 0; i--) {
          let p = particles[i]!;
          updateParticle(p);
          if (p.life <= 0) particles.splice(i, 1);
        }
      }

      ctx!.clearRect(0, 0, canvas!.width, canvas!.height);

      for (let r of rockets) drawRocket(ctx!, r);
      for (let p of particles) {
        if (p.life > 0) drawParticle(ctx!, p);
      }

      frameId = requestAnimationFrame(animate);
    };
    frameId = requestAnimationFrame(animate);

    return () => {
      clearTimeout(launchTimeout);
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', setCanvasSize);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [targetBounds]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{ pointerEvents: 'none' }}
    />
  );
};

export default Fireworks;
